import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { MultiValueProps } from "react-select";

import { Label } from "../../forms/Label";
import { Select, TSelectOption, TSelectProps } from "@/components/forms/Select";
import { asSelectComponents } from "@/components/forms/Select/asSelectComponents";

import { useDropdownPagination } from "@/hooks/filter-hooks/useDropdownPagination";
import { useDebounce } from "@/hooks/useDebounce";

import { PAGE_LIMIT_TWENTY } from "@/core/const";

import {
  TFilterDropdownConfig,
  TQueryHook,
  useFilterDropdownConfig,
} from "./filterDropdownRegistry";

import { TErrorData } from "@/core/types/error-fallback";
import { TBaseDataSource, TStandardPaginationData } from "@/types/filter";

const SEARCH_DEBOUNCE_MS = 500;
const LOADING_LABEL = "Loading...";
const LOADING_OPTION_VALUE = "__loading__";

const LOADING_OPTION: TSelectOption = {
  value: LOADING_OPTION_VALUE,
  label: LOADING_LABEL,
  isDisabled: true,
};

function appendLoadingLabel(option: TSelectOption): TSelectOption {
  if (!option.label || option.label === LOADING_LABEL) {
    return { ...option, label: LOADING_LABEL };
  }

  return {
    ...option,
    label: `${option.label} ${LOADING_LABEL}`,
  };
}

type TSelectOptions = TSelectOption[];

export type TFilterDropdownChangeMeta = {
  isSelectAll?: boolean;
};

type TGenericFilterDropdownProps<T = unknown> = {
  customPageSize?: number;
  dropdownType: string;
  dataSource?: TBaseDataSource<T[] | TStandardPaginationData<T>>;
  value?: string | string[];
  onChange: (
    value: string | string[],
    meta?: TFilterDropdownChangeMeta,
  ) => void;
  onOptionsChange?: (options: T[]) => void;
  accumulatedOptions?: T[];
  customLabel?: string;
  customPlaceholder?: string;
  disabled?: boolean;
  isLabelShown?: boolean;
  isMulti?: boolean;
  isSelectAll?: boolean;
  formatSelectedValuesLabel?: (options: TSelectOption[]) => ReactNode;
  selectedFallbackOptions?: TSelectOption[];
};

/**
 * Generic Filter Dropdown — Registry Pattern
 */
function GenericFilterDropdown<
  TItem = unknown,
  TParams extends Record<string, unknown> = Record<string, unknown>,
  TApiData = unknown,
>(props: TGenericFilterDropdownProps<TItem>) {
  const config = useFilterDropdownConfig(props.dropdownType);

  const [loadedQueryHook, setLoadedQueryHook] = useState<TQueryHook<
    TParams,
    TApiData,
    TErrorData
  > | null>(null);

  const [isLoadingHook, setIsLoadingHook] = useState<boolean>(true);

  useEffect(() => {
    if (!config?.loadQueryHook) {
      return;
    }

    setIsLoadingHook(true);

    void config
      .loadQueryHook()
      .then((hook) => {
        setLoadedQueryHook(
          () => hook as TQueryHook<TParams, TApiData, TErrorData>,
        );
      })
      .catch((err: unknown) => {
        console.error(`Failed to load hook for ${props.dropdownType}:`, err);
      })
      .finally(() => {
        setIsLoadingHook(false);
      });
  }, [config, props.dropdownType]);

  if (!config) {
    return null;
  }

  if (isLoadingHook || !loadedQueryHook) {
    return (
      <div className="w-full">
        {(props.isLabelShown ?? true) && (
          <Label weight="medium">{props.customLabel ?? config.labelKey}</Label>
        )}

        <div className="flex h-10 w-full items-center rounded-md border bg-gray-100 px-3 text-sm text-gray-400 animate-pulse dark:bg-gray-800">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <FilterDropdownContent
      {...props}
      config={config}
      useQueryHook={loadedQueryHook}
    />
  );
}

type TFilterDropdownContentProps<
  TItem,
  TParams extends Record<string, unknown>,
  TApiData,
> = TGenericFilterDropdownProps<TItem> & {
  config: TFilterDropdownConfig;
  useQueryHook: TQueryHook<TParams, TApiData, TErrorData>;
};

function FilterDropdownContent<
  TItem,
  TParams extends Record<string, unknown>,
  TApiData,
>({
  customPageSize,
  config,
  useQueryHook,
  dataSource,
  value,
  onChange,
  onOptionsChange,
  accumulatedOptions,
  customLabel,
  customPlaceholder,
  disabled = false,
  isLabelShown = true,
  isMulti = false,
  isSelectAll = false,
  dropdownType,
  formatSelectedValuesLabel,
  selectedFallbackOptions,
}: TFilterDropdownContentProps<TItem, TParams, TApiData>) {
  const { t } = useTranslation("common");

  const [searchInput, setSearchInput] = useState<string>("");

  const debouncedSearch = useDebounce(searchInput.trim(), SEARCH_DEBOUNCE_MS);

  const selectedOptionsCacheRef = useRef<Record<string, TSelectOption>>({});

  const pageSize = customPageSize ?? config.pageSize ?? PAGE_LIMIT_TWENTY;

  const supportsSearch = Boolean(config.supportsSearch);

  const parentDataArray = useMemo<TItem[] | undefined>(() => {
    const data = dataSource?.data;

    if (!data) {
      return undefined;
    }

    if (Array.isArray(data)) {
      return data;
    }

    if (
      typeof data === "object" &&
      "results" in data &&
      Array.isArray(data.results)
    ) {
      return data.results;
    }

    return undefined;
  }, [dataSource?.data]);

  // Create resetKey that combines apiParams changes + search
  // When apiParams change (filter dates, etc), resetKey changes → pagination resets
  const resetKey = useMemo(() => {
    // Hash apiParams to create a scope key
    const apiParamsKey = dataSource?.apiParams
      ? JSON.stringify(dataSource.apiParams)
      : "";

    // Only include search in resetKey when user has typed a query
    if (supportsSearch && debouncedSearch) {
      return apiParamsKey
        ? `${apiParamsKey}|${debouncedSearch}`
        : debouncedSearch;
    }

    return apiParamsKey || undefined;
  }, [dataSource?.apiParams, debouncedSearch, supportsSearch]);

  const pagination = useDropdownPagination<TItem>({
    parentData: parentDataArray,
    pageSize,
    onOptionsChange,
    accumulatedOptions,
    resetKey: resetKey,
  });

  const {
    appendPageResults,
    loadNextPage,
    pageSize: paginationPageSize,
    shouldFetchCurrentPage,
  } = pagination;

  const needsCurrentPage = useMemo(() => {
    if (pagination.currentPage === 1) {
      return false;
    }

    return shouldFetchCurrentPage();
  }, [pagination.currentPage, shouldFetchCurrentPage]);

  const shouldFetchSearchPage1 =
    supportsSearch && Boolean(debouncedSearch) && pagination.currentPage === 1;

  const shouldFetchInitialPage =
    !dataSource &&
    pagination.currentPage === 1 &&
    pagination.options.length === 0;

  const queryParams = useMemo<TParams>(() => {
    const base: Record<string, unknown> = {
      page: pagination.currentPage,
      page_size: paginationPageSize,
    };

    if (supportsSearch && debouncedSearch) {
      base.search = debouncedSearch;
    }

    // Merge defaultParams from config, then apiParams from dataSource, but let base override page and page_size
    const additionalApiParams = {
      ...(config.defaultParams ?? {}),
      ...(dataSource?.apiParams ?? {}),
    };

    return { ...additionalApiParams, ...base } as TParams;
  }, [
    config.defaultParams,
    debouncedSearch,
    pagination.currentPage,
    paginationPageSize,
    supportsSearch,
    dataSource?.apiParams,
  ]);

  const {
    data,
    isLoading: isInternalLoading,
    isFetching: isInternalFetching,
  } = useQueryHook(queryParams, {
    enabled:
      !disabled &&
      (needsCurrentPage || shouldFetchSearchPage1 || shouldFetchInitialPage) &&
      !(dataSource?.isLoading && !shouldFetchSearchPage1),
    ...config.queryOptions,
  });

  useEffect(() => {
    if (data) {
      appendPageResults(data);
    }
  }, [appendPageResults, data]);

  const handleLoadMore = useCallback(() => {
    loadNextPage(isInternalFetching);
  }, [isInternalFetching, loadNextPage]);

  const handleMenuOpen = useCallback(() => {
    // Optionally handle menu open
  }, []);

  const isAwaitingSearchDebounce =
    supportsSearch &&
    Boolean(searchInput.trim()) &&
    searchInput.trim() !== debouncedSearch;

  const isAwaitingSearchFetch =
    supportsSearch &&
    Boolean(debouncedSearch) &&
    (isInternalLoading || isInternalFetching) &&
    pagination.options.length === 0;

  const handleMenuClose = useCallback(() => {
    // Keep the pending search text while a search request is in-flight so the
    // user can reopen the menu and see the current query without a jump.
    if (supportsSearch && (isAwaitingSearchDebounce || isAwaitingSearchFetch)) {
      return;
    }

    setSearchInput("");
  }, [isAwaitingSearchDebounce, isAwaitingSearchFetch, supportsSearch]);

  const handleInputChange = useCallback(
    (value: string, actionMeta?: { action?: string }) => {
      // Keep the in-flight search visible while the fetch is pending.
      if (
        supportsSearch &&
        (actionMeta?.action === "input-blur" ||
          actionMeta?.action === "menu-close") &&
        (isAwaitingSearchDebounce || isAwaitingSearchFetch)
      ) {
        return;
      }

      // Allow react-select to clear input on blur/close
      if (
        actionMeta &&
        (actionMeta.action === "input-blur" ||
          actionMeta.action === "menu-close")
      ) {
        setSearchInput("");
        return;
      }
      setSearchInput(value);
    },
    [isAwaitingSearchDebounce, isAwaitingSearchFetch, supportsSearch],
  );

  const transformedValues = useMemo<TSelectOption[]>(() => {
    return pagination.options.map((item) => config.transformToOption(item));
  }, [config, pagination.options]);

  const isLoadingMore = isInternalFetching && pagination.options.length > 0;

  const isUnmapped = useMemo(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return false;
    }

    const ids = Array.isArray(value) ? value : [value];

    return ids.some((id) => !transformedValues.some((opt) => opt.value === id));
  }, [transformedValues, value]);

  const isSearchRequestPending =
    isAwaitingSearchDebounce || isAwaitingSearchFetch;

  const isFetchingOptions =
    (dataSource?.isLoading ?? false) ||
    ((isInternalLoading || isInternalFetching) &&
      pagination.options.length === 0);

  const isLoading =
    isFetchingOptions ||
    isLoadingMore ||
    isSearchRequestPending ||
    (isUnmapped && (dataSource?.isLoading ?? true));

  const showLoadingOption =
    !isLoadingMore &&
    (isFetchingOptions ||
      isAwaitingSearchFetch ||
      (isLoading && transformedValues.length === 0));

  const canLoadMore = pagination.hasMore && pagination.options.length > 0;

  // const selectAllOption = isMulti ? config.selectAllOption : undefined;
  const selectAllOption =
    isMulti && isSelectAll ? config.selectAllOption : undefined;
  const [isSelectAllMode, setIsSelectAllMode] = useState(false);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const isAllSelected = useMemo(() => {
    if (!selectAllOption || !Array.isArray(value)) {
      return false;
    }

    if (value.includes(selectAllOption.value) || isSelectAllMode) {
      return true;
    }

    if (transformedValues.length === 0) {
      return false;
    }

    const selectedValues = new Set(value);
    return transformedValues.every((option) =>
      selectedValues.has(option.value),
    );
  }, [isSelectAllMode, selectAllOption, transformedValues, value]);

  // Clear Select All mode when the controlled value is emptied externally
  useEffect(() => {
    if (!Array.isArray(value) || value.length === 0) {
      setIsSelectAllMode(false);
    }
  }, [value]);

  // When Select All is active, keep newly loaded pages included in the selection
  // so the closed-control label can update (e.g. "20 agents selected" → "40...").
  useEffect(() => {
    if (!isSelectAllMode || !isMulti || !selectAllOption) {
      return;
    }

    if (!Array.isArray(value) || transformedValues.length === 0) {
      return;
    }

    const selectedSet = new Set(value);
    const missing = transformedValues
      .map((option) => option.value)
      .filter((id) => !selectedSet.has(id));

    if (missing.length === 0) {
      return;
    }

    onChangeRef.current([...value, ...missing], { isSelectAll: true });
  }, [isMulti, isSelectAllMode, selectAllOption, transformedValues, value]);

  const resolveSelectedOption = useCallback(
    (val: string): TSelectOption => {
      const found = transformedValues.find((item) => item.value === val);

      if (found) {
        selectedOptionsCacheRef.current[val] = found;
        return found;
      }

      const cached = selectedOptionsCacheRef.current[val];
      if (cached?.label && cached.label !== LOADING_LABEL) {
        return cached;
      }

      const fallback = selectedFallbackOptions?.find(
        (item) => item.value === val,
      );

      if (fallback?.label && fallback.label !== LOADING_LABEL) {
        selectedOptionsCacheRef.current[val] = fallback;
        return fallback;
      }

      if (isLoading) {
        return {
          value: val,
          label: LOADING_LABEL,
        };
      }

      return {
        value: val,
        label: val,
      };
    },
    [isLoading, selectedFallbackOptions, transformedValues],
  );

  const resolvedSelectedOption = useMemo<
    TSelectOption | TSelectOption[] | null
  >(() => {
    if (!value) {
      return isMulti ? [] : null;
    }

    if (isMulti && Array.isArray(value)) {
      return value.map((val) => resolveSelectedOption(val));
    }

    if (!isMulti && typeof value === "string") {
      return resolveSelectedOption(value);
    }

    return isMulti ? [] : null;
  }, [isMulti, resolveSelectedOption, value]);

  const selectedOption = useMemo<TSelectOption | TSelectOption[] | null>(() => {
    // Select All: keep the label as "Select All"
    if (isSelectAllMode && selectAllOption && isMulti) {
      return [selectAllOption];
    }

    if (isAllSelected && selectAllOption) {
      return isMulti ? [selectAllOption] : selectAllOption;
    }

    if (!isFetchingOptions || !resolvedSelectedOption) {
      return resolvedSelectedOption;
    }

    if (Array.isArray(resolvedSelectedOption)) {
      return resolvedSelectedOption.map(appendLoadingLabel);
    }

    return appendLoadingLabel(resolvedSelectedOption);
  }, [
    isAllSelected,
    isFetchingOptions,
    isMulti,
    isSelectAllMode,
    resolvedSelectedOption,
    selectAllOption,
  ]);

  const optionsForSelect = useMemo<TSelectOptions>(() => {
    const selectedMap = new Map<string, TSelectOption>();

    const normalizedSelectedOptions = Array.isArray(resolvedSelectedOption)
      ? resolvedSelectedOption
      : resolvedSelectedOption
        ? [resolvedSelectedOption]
        : [];

    normalizedSelectedOptions.forEach((opt) => {
      if (opt?.value) {
        selectedMap.set(opt.value, opt);
      }
    });

    const selectedOptionsArray = Array.from(selectedMap.values());

    const persistableSelectedOptions = selectedOptionsArray.filter(
      (sel) =>
        sel.label && sel.label !== LOADING_LABEL && sel.label !== sel.value, // exclude unresolved "raw id as label" fallback entries
    );

    const remainingOptions = transformedValues.filter(
      (opt) =>
        !persistableSelectedOptions.some((sel) => sel.value === opt.value),
    );

    const options: TSelectOption[] = [];

    // Select All sits above everything else, same as any selected option would.
    if (selectAllOption) {
      options.push(selectAllOption);
    }

    if (persistableSelectedOptions.length > 0) {
      options.push(...persistableSelectedOptions);
    }

    if (remainingOptions.length > 0) {
      options.push(...remainingOptions);
    }

    // Keep selected values at the top of the list while preserving the footer
    // loading indicator for paginated fetches, which avoids scroll jumps.
    if (showLoadingOption) {
      options.push(LOADING_OPTION);
    }

    return options;
  }, [
    resolvedSelectedOption,
    selectAllOption,
    showLoadingOption,
    transformedValues,
  ]);

  const multiValueSummaryComponents = useMemo(() => {
    if (!isMulti || (!formatSelectedValuesLabel && !isSelectAllMode)) {
      return undefined;
    }

    const MultiValue = (props: MultiValueProps<TSelectOption, boolean>) => {
      if (props.index > 0) {
        return null;
      }

      // Select All only: show Select All
      if (isSelectAllMode && selectAllOption) {
        return (
          <div className="min-w-0 truncate px-1 text-sm">
            {selectAllOption.label}
          </div>
        );
      }

      if (!formatSelectedValuesLabel) {
        return null;
      }

      return (
        <div className="min-w-0 truncate px-1 text-sm">
          {formatSelectedValuesLabel([...props.getValue()])}
        </div>
      );
    };

    return asSelectComponents({ MultiValue });
  }, [formatSelectedValuesLabel, isMulti, isSelectAllMode, selectAllOption]);

  const selectStyles = useMemo<TSelectProps["styles"]>(
    () => ({
      menuList: (base) => ({
        ...base,
        maxHeight: "230px",
        minHeight: "100px",
        overflow: "auto",
      }),

      group: (base) => ({
        ...base,
        padding: "4px 0",
      }),

      valueContainer: (base) => ({
        ...base,
        ...(isMulti && (formatSelectedValuesLabel || isSelectAllMode)
          ? {
              flexWrap: "nowrap",
              overflow: "hidden",
            }
          : {}),
      }),

      input: (base) => ({
        ...base,
        ...(isMulti && (formatSelectedValuesLabel || isSelectAllMode)
          ? {
              margin: 0,
              padding: 0,
            }
          : {}),
      }),
    }),
    [formatSelectedValuesLabel, isMulti, isSelectAllMode],
  );

  const label = customLabel ?? t(config.labelKey, "Select...");

  const hasSelectedValue =
    value !== undefined &&
    value !== null &&
    (Array.isArray(value) ? value.length > 0 : value !== "");

  const placeholder = hasSelectedValue
    ? undefined
    : (customPlaceholder ?? t(config.placeholderKey, "Select..."));

  return (
    <div className="w-full">
      {isLabelShown && (
        <Label weight="medium" className="mb-1">
          {" "}
          {label}{" "}
        </Label>
      )}

      <div
        onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
          e.stopPropagation();
        }}
      >
        <Select
          name={dropdownType}
          options={optionsForSelect as TSelectOption[]}
          value={selectedOption}
          onChange={(opt: TSelectOption | TSelectOption[] | null) => {
            if (isMulti) {
              const selected = (Array.isArray(opt) ? opt : []).filter(
                (option) => option?.value !== LOADING_OPTION_VALUE,
              );
              const choseSelectAll = selected.some(
                (option) => option?.value === selectAllOption?.value,
              );
              const selectedValues = choseSelectAll
                ? transformedValues.map((option) => option.value)
                : selected
                    .map((option) => option.value)
                    .filter((id) => id !== selectAllOption?.value);

              setIsSelectAllMode(choseSelectAll);

              selected.forEach((option) => {
                if (
                  option?.value &&
                  option.label !== LOADING_LABEL &&
                  option.value !== selectAllOption?.value
                ) {
                  selectedOptionsCacheRef.current[option.value] = option;
                }
              });
              onChange(selectedValues, { isSelectAll: choseSelectAll });

              return;
            }

            const selected = (opt as TSelectOption | null) ?? null;

            if (selected?.value === LOADING_OPTION_VALUE) {
              return;
            }

            if (selected?.value && selected.label !== LOADING_LABEL) {
              selectedOptionsCacheRef.current[selected.value] = selected;
            }

            onChange(selected?.value ?? "");
          }}
          getOptionLabel={(option: TSelectOption) => option.label}
          getOptionValue={(option: TSelectOption) => option.value}
          placeholder={placeholder}
          className="text-sm"
          isClearable
          isMulti={isMulti}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          hasNextPage={canLoadMore}
          onLoadMore={canLoadMore ? handleLoadMore : undefined}
          isDisabled={disabled}
          isSearchable={supportsSearch}
          inputValue={supportsSearch ? searchInput : undefined}
          onInputChange={supportsSearch ? handleInputChange : undefined}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          filterOption={supportsSearch ? null : undefined}
          noOptionsMessage={() =>
            showLoadingOption
              ? t("LOADING", LOADING_LABEL)
              : t("NO_OPTIONS", "No options")
          }
          components={multiValueSummaryComponents}
          styles={selectStyles}
        />
      </div>
    </div>
  );
}

export default GenericFilterDropdown;
